import { PrismicRichText } from '@prismicio/react';
import { defaultsDeep } from 'lodash';
import { ComponentProps, ReactNode } from 'react';
import { getRoute } from '../../routes';
import { Heading, Link, Text, TextList } from '../core';
import { HeadingLevel } from '../core/Heading';
import type {
  RTHeading1Node,
  RTHeading2Node,
  RTHeading3Node,
  RTHeading4Node,
  RTHeading5Node,
  RTHeading6Node,
  RTLabelNode,
} from '@prismicio/types';
import type { RTLinkNode } from '@prismicio/types/src/value/richText';
import type { StyledProps } from 'native-base';

/**
 * Text - Put on every element with actual text inside it.
 * Heading - Put on every heading element.
 */
type StylesOverride = {
  text?: StyledProps;
  heading?: StyledProps;
  heading1?: StyledProps;
  heading2?: StyledProps;
  heading3?: StyledProps;
  heading4?: StyledProps;
  heading5?: StyledProps;
  heading6?: StyledProps;
  list?: StyledProps;
  listItem?: StyledProps;
  paragraph?: StyledProps;
  link?: StyledProps;
};

type RichTextProps = ComponentProps<typeof PrismicRichText> & {
  styles?: StylesOverride;
};

export const RichText = (props: RichTextProps): JSX.Element => {
  const overrideStyles = defaultsDeep(props.styles, {
    heading: {
      marginBottom: 4,
    },
    paragraph: {
      marginBottom: 4,
    },
    list: {
      marginBottom: 4,
      space: 3,
    },
  });

  return <PrismicRichText components={richTextComponentsMap(overrideStyles)} {...props} />;
};

type SubProps = { children: ReactNode; key: string };
type Heading1Props = SubProps & { node: RTHeading1Node & { data?: RTLabelNode['data'] } };
type Heading2Props = SubProps & { node: RTHeading2Node & { data?: RTLabelNode['data'] } };
type Heading3Props = SubProps & { node: RTHeading3Node & { data?: RTLabelNode['data'] } };
type Heading4Props = SubProps & { node: RTHeading4Node & { data?: RTLabelNode['data'] } };
type Heading5Props = SubProps & { node: RTHeading5Node & { data?: RTLabelNode['data'] } };
type Heading6Props = SubProps & { node: RTHeading6Node & { data?: RTLabelNode['data'] } };
type LabelProps = SubProps & { node: RTLabelNode };
type LinkProps = SubProps & { node: RTLinkNode };

const getHeadingLevel = (
  label: string | undefined,
  defaultLevel?: HeadingLevel,
): HeadingLevel | undefined => {
  if (label === 'semanticP') {
    return undefined;
  }
  if (label === 'semanticH1') {
    return 1;
  }
  if (label === 'semanticH2') {
    return 2;
  }
  if (label === 'semanticH3') {
    return 3;
  }
  if (label === 'semanticH4') {
    return 4;
  }
  if (label === 'semanticH5') {
    return 5;
  }
  if (label === 'semanticH6') {
    return 6;
  }
  return defaultLevel;
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const richTextComponentsMap = (styles: StylesOverride) => ({
  heading1: ({ children, key, node }: Heading1Props) => (
    <Heading.h1
      level={getHeadingLevel(node.data?.label, undefined)}
      {...styles.text}
      {...styles.heading}
      {...styles.heading1}
      key={key}
    >
      {children}
    </Heading.h1>
  ),
  heading2: ({ children, key, node }: Heading2Props) => (
    <Heading.h2
      level={getHeadingLevel(node.data?.label, undefined)}
      {...styles.text}
      {...styles.heading}
      {...styles.heading2}
      key={key}
    >
      {children}
    </Heading.h2>
  ),
  heading3: ({ children, key, node }: Heading3Props) => (
    <Heading.h3
      level={getHeadingLevel(node.data?.label, undefined)}
      {...styles.text}
      {...styles.heading}
      {...styles.heading3}
      key={key}
    >
      {children}
    </Heading.h3>
  ),
  heading4: ({ children, key, node }: Heading4Props) => (
    <Heading.h4
      level={getHeadingLevel(node.data?.label, undefined)}
      {...styles.text}
      {...styles.heading}
      {...styles.heading4}
      key={key}
    >
      {children}
    </Heading.h4>
  ),
  heading5: ({ children, key, node }: Heading5Props) => (
    <Heading.h5
      level={getHeadingLevel(node.data?.label, undefined)}
      {...styles.text}
      {...styles.heading}
      {...styles.heading5}
      key={key}
    >
      {children}
    </Heading.h5>
  ),
  heading6: ({ children, key, node }: Heading6Props) => (
    <Heading.h6
      level={getHeadingLevel(node.data?.label, 2)}
      {...styles.text}
      {...styles.heading}
      {...styles.heading6}
      key={key}
    >
      {children}
    </Heading.h6>
  ),
  paragraph: ({ children, key }: SubProps) => (
    <Text.para {...styles.text} {...styles.paragraph} key={key}>
      {children}
    </Text.para>
  ),

  list: ({ children, key }: SubProps) => (
    <TextList.List {...styles.list} key={key}>
      {children}
    </TextList.List>
  ),
  oList: ({ children, key }: SubProps) => (
    <TextList.List {...styles.list} key={key}>
      {children}
    </TextList.List>
  ),
  listItem: ({ children, key }: SubProps) => (
    <TextList.Item {...styles.listItem} key={key}>
      {children}
    </TextList.Item>
  ),
  oListItem: ({ children, key }: SubProps) => (
    <TextList.Item {...styles.listItem} key={key}>
      {children}
    </TextList.Item>
  ),

  strong: ({ children, key }: SubProps) => (
    <Text.Text {...styles.text} key={key} bold>
      {children}
    </Text.Text>
  ),
  // eslint-disable-next-line id-length
  em: ({ children, key }: SubProps) => (
    <Text.Text {...styles.text} key={key} fontStyle="italic">
      {children}
    </Text.Text>
  ),
  hyperlink: ({ children, key, node }: LinkProps) => {
    const linkType = node.data.link_type;

    let url = '';
    if (linkType === 'Web' || linkType === 'Media') {
      url = node.data.url ?? '';

      if (url.includes(':///')) {
        url = `/${url.split(':///')[1] ?? ''}`;
      }
    } else if (linkType === 'Document' && node.data.type.toLowerCase() === 'module') {
      url = getRoute('module', { moduleId: node.data.id });
    }

    if (!url) {
      return (
        <Text.para {...styles.text} {...styles.paragraph} key={key}>
          {children}
        </Text.para>
      );
    }

    return (
      <Link.para {...styles.text} {...styles.link} to={url} key={key}>
        {children}
      </Link.para>
    );
  },
  label: ({ children, key, node }: LabelProps) => {
    if (node.data.label === 'underline') {
      return (
        <Text.Text {...styles.text} key={key} textDecorationLine="underline">
          {children}
        </Text.Text>
      );
    }
    return (
      <Text.Text {...styles.text} key={key}>
        {children}
      </Text.Text>
    );
  },
});
